<div class="salary-management-container">
  <mat-card>
    <mat-card-header>
      <mat-card-title>
        <mat-icon>attach_money</mat-icon>
        Salary Management
      </mat-card-title>
      <mat-card-subtitle>Calculate and track employee salaries</mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
      <mat-tab-group>
        <mat-tab label="Salary Calculation">
          <!-- Salary Calculation Content: Only filter form and summary cards -->
          <div>
            <!-- Filter Form -->
            <div class="filter-section">
              <form [formGroup]="filterForm" class="filter-form">
                <div class="filter-row">
                  <mat-form-field appearance="outline">
                    <mat-label>Employee</mat-label>
                    <mat-select formControlName="employeeId" (selectionChange)="onFilterChange()">
                      <mat-option value="">All Employees</mat-option>
                      <mat-option *ngFor="let employee of employees" [value]="employee.id">
                        {{ employee.fullName }}
                      </mat-option>
                    </mat-select>
                    <mat-icon matSuffix>person</mat-icon>
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>Year</mat-label>
                    <input matInput type="number" formControlName="year" (change)="onFilterChange()">
                    <mat-icon matSuffix>calendar_today</mat-icon>
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>Month</mat-label>
                    <mat-select formControlName="month" (selectionChange)="onFilterChange()">
                      <mat-option *ngFor="let month of months" [value]="month.value">
                        {{ month.label }}
                      </mat-option>
                    </mat-select>
                    <mat-icon matSuffix>date_range</mat-icon>
                  </mat-form-field>

                  <mat-form-field appearance="outline">
                    <mat-label>Status</mat-label>
                    <mat-select formControlName="status" (selectionChange)="onFilterChange()">
                      <mat-option value="">All Statuses</mat-option>
                      <mat-option *ngFor="let status of paymentStatuses" [value]="status.value">
                        {{ status.label }}
                      </mat-option>
                    </mat-select>
                    <mat-icon matSuffix>payment</mat-icon>
                  </mat-form-field>

                  <button mat-raised-button color="primary" (click)="calculateSalary()" [disabled]="loading || !filterForm.get('year')?.value || !filterForm.get('month')?.value">
                    <mat-icon>calculate</mat-icon>
                    Calculate Salary
                  </button>
                </div>
              </form>
            </div>
            <!-- Employee Summary Grid -->
            <div class="salary-summary-grid" *ngIf="salarySummaries.length > 0">
              <div class="salary-summary-card" *ngFor="let summary of salarySummaries">
                <div class="employee-name">{{ summary.employeeName }}</div>
                <div class="salary-amount">
                  £{{ summary.totalAmount | number:'1.2-2' }}
                  <span class="salary-label">Total</span>
                </div>
                <div class="salary-details">
                  <span class="detail"><mat-icon>schedule</mat-icon> {{ summary.hoursWorked | number:'1.2-2' }} hrs</span>
                  <span class="detail"><mat-icon>calendar_today</mat-icon> {{ summary.daysWorked }} days</span>
                  <span class="detail"><mat-icon>payments</mat-icon> {{ summary.salaryType }}</span>
                  <span class="detail"><mat-icon>calendar_month</mat-icon> {{ summary.period }}</span>
                </div>
                <button mat-stroked-button color="primary" class="make-payment-btn" (click)="openSalaryPaymentDialog(summary)">
                  <mat-icon>payments</mat-icon> Make Payment
                </button>
              </div>
            </div>
          </div>
        </mat-tab>
        <mat-tab label="Salary History">
          <div class="salary-history-section">
            <form [formGroup]="historyForm" class="history-filter-form" (ngSubmit)="getSalaryPayments()">
              <mat-form-field appearance="outline" class="history-field">
                <mat-label>Year</mat-label>
                <input matInput formControlName="year" type="number" min="2000" max="2100" />
              </mat-form-field>
              <mat-form-field appearance="outline" class="history-field">
                <mat-label>Month</mat-label>
                <mat-select formControlName="month">
                  <mat-option *ngFor="let m of months" [value]="m.value">{{ m.label }}</mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline" class="history-field">
                <mat-label>Status</mat-label>
                <mat-select formControlName="status">
                  <mat-option value="">All Statuses</mat-option>
                  <mat-option *ngFor="let s of paymentStatuses" [value]="s.value">{{ s.label }}</mat-option>
                </mat-select>
              </mat-form-field>
              <button mat-raised-button color="primary" type="submit" [disabled]="historyForm.invalid || loading">Get Salary Payments</button>
            </form>
            <div class="salary-history-table" *ngIf="salaryPayments.length > 0">
              <table mat-table [dataSource]="salaryPayments" class="mat-elevation-z1">
                <ng-container matColumnDef="employeeName">
                  <th mat-header-cell *matHeaderCellDef>Employee</th>
                  <td mat-cell *matCellDef="let row">{{ row.employeeName }}</td>
                </ng-container>
                <ng-container matColumnDef="year">
                  <th mat-header-cell *matHeaderCellDef>Year</th>
                  <td mat-cell *matCellDef="let row">{{ row.year }}</td>
                </ng-container>
                <ng-container matColumnDef="month">
                  <th mat-header-cell *matHeaderCellDef>Month</th>
                  <td mat-cell *matCellDef="let row">{{ getMonthLabel(row.month) }}</td>
                </ng-container>
                <ng-container matColumnDef="amount">
                  <th mat-header-cell *matHeaderCellDef>Amount</th>
                  <td mat-cell *matCellDef="let row">£{{ row.amount | number:'1.2-2' }}</td>
                </ng-container>
                <ng-container matColumnDef="hoursWorked">
                  <th mat-header-cell *matHeaderCellDef>Hours Worked</th>
                  <td mat-cell *matCellDef="let row">{{ row.hoursWorked | number:'1.2-2' }}</td>
                </ng-container>
                <ng-container matColumnDef="daysWorked">
                  <th mat-header-cell *matHeaderCellDef>Days Worked</th>
                  <td mat-cell *matCellDef="let row">{{ row.daysWorked }}</td>
                </ng-container>
                <ng-container matColumnDef="status">
                  <th mat-header-cell *matHeaderCellDef>Status</th>
                  <td mat-cell *matCellDef="let row">
                    <span class="status-badge" [ngClass]="getStatusClass(row.status)">{{ getStatusLabel(row.status) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="paymentDate">
                  <th mat-header-cell *matHeaderCellDef>Payment Date</th>
                  <td mat-cell *matCellDef="let row">{{ row.paymentDate | date:'dd/MM/yyyy' }}</td>
                </ng-container>
                <ng-container matColumnDef="paymentMethod">
                  <th mat-header-cell *matHeaderCellDef>Method</th>
                  <td mat-cell *matCellDef="let row">{{ row.paymentMethod }}</td>
                </ng-container>
                <ng-container matColumnDef="referenceNumber">
                  <th mat-header-cell *matHeaderCellDef>Reference</th>
                  <td mat-cell *matCellDef="let row">{{ row.referenceNumber }}</td>
                </ng-container>
                <ng-container matColumnDef="notes">
                  <th mat-header-cell *matHeaderCellDef>Notes</th>
                  <td mat-cell *matCellDef="let row">{{ row.notes }}</td>
                </ng-container>
                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef>Actions</th>
                  <td mat-cell *matCellDef="let row">
                    <button mat-icon-button color="warn" (click)="deleteSalaryPayment(row)">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="salaryHistoryColumns.concat(['actions'])"></tr>
                <tr mat-row *matRowDef="let row; columns: salaryHistoryColumns.concat(['actions']);"></tr>
              </table>
            </div>
            <div *ngIf="salaryPayments.length === 0 && historySearched" class="no-history-msg">
              No salary payments found for the selected criteria.
            </div>
          </div>
        </mat-tab>
      </mat-tab-group>
    </mat-card-content>
  </mat-card>
</div> 